import React from 'react';

import { daysAt, fmtDays, rangeIndexForTrade } from './ranges';
import {
  activePresetForUi,
  cardById,
  exactMatch,
  preferChosenWhenSameScopes,
  suggestedPresetForUi
} from './resolve';
import { TatStyles as S } from './styles';

/**
 * B1–B3 — preset ladder at the top of a multi-preset trade.
 * Selected = explicit preset click (not highlighted).
 * Applicable = inferred from picks; highlighted and drives the TAT card.
 */
const PresetBlock = ({
  cfg,
  resolved,
  picks,
  chosenPresetId,
  typicalStdIdx,
  onPickPreset,
  onJumpToTat,
  readOnly,
  blockRef
}) => {
  if (!cfg || cfg.presets.length <= 1) return null;

  const colIdx = rangeIndexForTrade(cfg, typicalStdIdx);
  const uiActive = activePresetForUi(cfg, picks, chosenPresetId, resolved?.preset);
  const chosen =
    chosenPresetId != null && chosenPresetId !== ''
      ? cfg.presets.find(p => String(p.id) === String(chosenPresetId)) || null
      : null;
  const applicable = preferChosenWhenSameScopes(
    cfg,
    chosen,
    suggestedPresetForUi(cfg, picks, chosenPresetId),
    picks
  );
  let status = 'Nothing picked yet — choose a preset or tick items below.';
  if (applicable) {
    status = `TAT card applicable: “${applicable.name}”.`;
    if (uiActive && String(uiActive.id) !== String(applicable.id)) {
      status = `TAT card applicable: “${applicable.name}” (you selected “${uiActive.name}”).`;
    } else if (uiActive && exactMatch(cfg, uiActive, picks)) {
      status = `Your picks match “${uiActive.name}” — TAT card applicable.`;
    }
  } else if (uiActive) {
    if (resolved && !resolved.exact) {
      status = `Customised from “${resolved.preset.name}” — still on the ${resolved.card.name} card.`;
    } else {
      status = `On “${uiActive.name}”.`;
    }
  }

  return (
    <S.PresetCard ref={blockRef}>
      <S.PresetHead>
        <S.PresetHeadCopy>
          <S.PresetTitle>Start from a scope preset</S.PresetTitle>
          <S.PresetSub>
            Pick one and it ticks the matching items below. Each preset maps to a turnaround card
            {onJumpToTat ? (
              <>
                {' '}
                —{' '}
                <S.PresetJump type='button' onClick={onJumpToTat}>
                  see the impact at the end of this trade ↓
                </S.PresetJump>
              </>
            ) : null}
            .
          </S.PresetSub>
        </S.PresetHeadCopy>
        <S.PresetStatus>{status}</S.PresetStatus>
      </S.PresetHead>

      <S.PresetGrid role='radiogroup' aria-label='Scope presets'>
        {cfg.presets.map((p, i) => {
          const isSelected = String(uiActive?.id) === String(p.id);
          const isApplicable = String(applicable?.id) === String(p.id);
          const cust = !!(resolved && !resolved.exact && isApplicable);
          const pCard = cardById(cfg, p.cardId);
          const pDays = daysAt(pCard, colIdx, { side: 'customer' });
          const daysText =
            colIdx == null
              ? 'Days once you pick a sheet range'
              : pDays == null
                ? 'Quoted on acceptance'
                : `${fmtDays(pDays)} at ${cfg.ranges[colIdx]} sheets`;
          let stateLabel = 'Select';
          if (isApplicable) stateLabel = 'TAT card applicable';
          else if (isSelected) stateLabel = 'Selected';
          return (
            <S.PresetTile
              key={p.id}
              type='button'
              role='radio'
              aria-checked={isApplicable}
              $matched={isApplicable}
              $custom={cust}
              disabled={readOnly}
              onClick={() => !readOnly && onPickPreset && onPickPreset(p)}>
              <S.PresetTileTop>
                <S.PresetN>Preset {i + 1}</S.PresetN>
                <S.PresetState $matched={isApplicable} $custom={cust}>
                  {stateLabel}
                </S.PresetState>
              </S.PresetTileTop>
              <S.PresetName>{p.name}</S.PresetName>
              {p.desc ? <S.PresetDesc>{p.desc}</S.PresetDesc> : null}
              <S.PresetMeta>
                <span>Card: {pCard?.name || '—'}</span>
                <S.PresetDays $matched={isApplicable}>{daysText}</S.PresetDays>
              </S.PresetMeta>
            </S.PresetTile>
          );
        })}
      </S.PresetGrid>
    </S.PresetCard>
  );
};

export default PresetBlock;
